/**
 * What an architect has to set up in Revit for Loopbuild to price their model.
 *
 * `name` is not cosmetic: it is the exact schedule column header the API looks for.
 * loopbuild-api lowercases attribute names but does not fold separators, so
 * "LB_Angle" arrives as the key `lb_angle`, which is what fitting.yaml and pipe.yaml
 * list under `sources`. Renaming a parameter here without changing those schemas
 * (and EstimationsController's angle group key) silently stops it being read.
 *
 * `guid` is the identity Revit stores in the project. It must never change once a
 * project has bound the parameter, or every model out there loses its values.
 *
 * To add a domain (ducting, cable tray, ...): add a CATEGORIES entry, list its id in
 * the `appliesTo` of the parameters and columns it needs, and add the matching
 * RevitIntegrationPage.category.* translations. The card, the detail page and the
 * download all follow from that.
 */

// The card grid, in display order. `id` is the URL segment: /revit-integration/pipes.
export const CATEGORIES = [
  { id: 'pipes', fileName: 'LoopbuildPipes.txt' },
  { id: 'fittings', fileName: 'LoopbuildPipeFittings.txt' },
  { id: 'accessories', fileName: 'LoopbuildPipeAccessories.txt' },
];

export const ALL_FILE_NAME = 'LoopbuildSharedParameters.txt';

// binding: 'type' | 'instance', which radio to pick in Manage > Project Parameters.
// fileDescription is what Revit shows in the shared parameter browser, so it repeats
// the binding advice: whoever loads the file is not necessarily looking at this page.
export const SHARED_PARAMETERS = [
  {
    name: 'LB_Material',
    guid: 'd592c80c-78e9-43ff-bb96-1471c564cbf0',
    required: true,
    binding: 'type',
    appliesTo: ['pipes', 'fittings', 'accessories'],
    example: 'PVC-U',
    descriptionId: 'RevitIntegrationPage.param.materialDescription',
    fileDescription:
      'REQUIRED. Material family of the pipe or fitting: PVC-U, CPVC, ABS, PE100, PP, copper, steel, cast iron. Bind to Pipes, Pipe Fittings and Pipe Accessories (Type parameter). Without this, Loopbuild cannot match the element to any product.',
  },
  {
    name: 'LB_Angle',
    guid: '9a0ad8df-1d2e-4939-8291-fc8472005f93',
    required: true,
    binding: 'instance',
    appliesTo: ['fittings'],
    example: '90',
    descriptionId: 'RevitIntegrationPage.param.angleDescription',
    fileDescription:
      'REQUIRED for elbows, bends and tees. The angle in degrees: 45 or 90. Bind to Pipe Fittings (Instance parameter). Without this, a 45 deg and a 90 deg of the same size collapse into a single estimate line.',
  },
  {
    name: 'LB_Size_Secondary',
    guid: '1a3ee1f2-c2f6-4acf-a165-17fdc51f4569',
    required: false,
    binding: 'instance',
    appliesTo: ['fittings'],
    example: '63',
    descriptionId: 'RevitIntegrationPage.param.sizeSecondaryDescription',
    fileDescription:
      'Reduced outlet in millimetres, for reducers, reducing tees and reducing bushes only. Plain number, e.g. 63. Bind to Pipe Fittings (Instance parameter). Leave empty on equal fittings.',
  },
  {
    name: 'LB_Pressure_Rating',
    guid: '9825a914-388d-4f1a-a4d3-775afd26549d',
    required: false,
    binding: 'type',
    appliesTo: ['pipes', 'fittings'],
    example: 'PN16',
    descriptionId: 'RevitIntegrationPage.param.pressureRatingDescription',
    fileDescription:
      'Pressure rating, e.g. PN16 or 10 bar. Bind to Pipes and Pipe Fittings (Type parameter). Optional: improves ranking between otherwise identical products.',
  },
  {
    name: 'LB_Connection_Type',
    guid: '1276e878-b941-424f-8ca4-7539eb435296',
    required: false,
    binding: 'type',
    appliesTo: ['pipes', 'fittings'],
    example: 'solvent weld',
    descriptionId: 'RevitIntegrationPage.param.connectionTypeDescription',
    fileDescription:
      'Jointing method: solvent weld, threaded, push-fit, compression or flanged. Bind to Pipes and Pipe Fittings (Type parameter). Optional: improves ranking between otherwise identical products.',
  },
];

// Built-in Revit fields, nothing to create, they just have to be columns in the
// schedule. Left out of the .txt on purpose: Revit refuses a project parameter whose
// name collides with a built-in one.
export const SCHEDULE_COLUMNS = [
  {
    name: 'Family and Type',
    appliesTo: ['pipes', 'fittings', 'accessories'],
    descriptionId: 'RevitIntegrationPage.column.familyAndTypeDescription',
  },
  {
    name: 'Size',
    appliesTo: ['pipes', 'fittings', 'accessories'],
    descriptionId: 'RevitIntegrationPage.column.sizeDescription',
  },
  {
    name: 'System Classification',
    appliesTo: ['pipes', 'fittings', 'accessories'],
    descriptionId: 'RevitIntegrationPage.column.systemClassificationDescription',
  },
  {
    name: 'Length',
    appliesTo: ['pipes'],
    descriptionId: 'RevitIntegrationPage.column.lengthDescription',
  },
  {
    name: 'Count',
    appliesTo: ['pipes', 'fittings', 'accessories'],
    descriptionId: 'RevitIntegrationPage.column.countDescription',
  },
];

export const SETUP_STEPS = [
  'RevitIntegrationPage.step.download',
  'RevitIntegrationPage.step.loadFile',
  'RevitIntegrationPage.step.bindParameters',
  'RevitIntegrationPage.step.fillValues',
  'RevitIntegrationPage.step.buildSchedule',
  'RevitIntegrationPage.step.export',
];

export const forCategory = (items, categoryId) =>
  categoryId ? items.filter(i => i.appliesTo.includes(categoryId)) : items;

/**
 * Revit's shared parameter format: tab-delimited, one PARAM row per parameter, and a
 * fixed *-prefixed header row per section. Generated rather than shipped as a static
 * file so a per-category download is free and the GUIDs have one home.
 */
export const buildSharedParameterFile = params =>
  [
    '# This is a Revit shared parameter file.',
    '# Do not edit manually.',
    '#',
    '# Loopbuild - https://loopbuild.com/revit-integration',
    '# Load via Manage > Shared Parameters > Browse, then bind each parameter as a',
    '# Project Parameter (Manage > Project Parameters > Add) to the categories listed',
    '# in each description.',
    '#',
    '# Every parameter is TEXT on purpose. LENGTH and ANGLE parameters are exported in',
    '# whatever unit the project is set to ("0.06 m" instead of "63 mm"), which silently',
    '# changes the number Loopbuild reads. Type plain values: 63, 90, PN16.',
    '*META\tVERSION\tMINVERSION',
    'META\t2\t1',
    '*GROUP\tID\tNAME',
    'GROUP\t1\tLoopbuild',
    '*PARAM\tGUID\tNAME\tDATATYPE\tDATACATEGORY\tGROUP\tVISIBLE\tDESCRIPTION\tUSERMODIFIABLE\tHIDEWHENNOVALUE',
    ...params.map(p => `PARAM\t${p.guid}\t${p.name}\tTEXT\t\t1\t1\t${p.fileDescription}\t1\t0`),
  ].join('\n') + '\n';
